/**
 * useMergedProductsData - dual-mode data fetching hook
 * Mode A (no issueFilter): Products primary + Readiness secondary
 * Mode B (issueFilter active): Readiness primary (filtered)
 * Plan 012
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SortingState } from '@tanstack/react-table'
import { getProducts } from '@/lib/api/products'
import { useReadinessProducts } from '@/hooks/useChannelReadiness'
import { usePackshotStatuses, useAIReferenceSummary } from '@/hooks/useImageIntelligence'
import type { IssueFilter } from '@/lib/api/channel-readiness'
import type { ProductWithReadiness } from '@/types/products'
import type { ProductTableFilters } from './useProductTableConfig'
import { buildReadinessMap, mergeProduct, mergeFromScore } from './mergedProductsUtils'

export const MERGED_PAGE_SIZE = 25

export function useMergedProductsData(
  channelId: string | null,
  page: number,
  search: string,
  sorting: SortingState,
  issueFilter: IssueFilter | null,
  imageStatusFilter: string[],
  filters: ProductTableFilters = { is_active: true },
) {
  const isFiltered = !!issueFilter || imageStatusFilter.length > 0

  // Mode A: Products primary
  const productsQ = useQuery({
    queryKey: ['products', { page, search, sort: sorting[0], filters }],
    queryFn: () => getProducts({
      page, page_size: MERGED_PAGE_SIZE, search: search || undefined,
      include: 'sales_metrics',
      sort_by: sorting[0]?.id, sort_dir: sorting[0]?.desc ? 'desc' : 'asc',
      is_active: filters.is_active,
      quantity_30d_min: filters.quantity_30d_min,
      revenue_30d_min: filters.revenue_30d_min,
      quantity_90d_min: filters.quantity_90d_min,
      revenue_90d_min: filters.revenue_90d_min,
    }),
    enabled: !isFiltered,
    staleTime: 30_000,
  })

  // Mode A: Readiness enrichment (full page, no pagination)
  const readinessQ = useReadinessProducts(!isFiltered ? channelId : null, { page_size: 500 })

  // Mode B: Readiness primary with issue filter or image status filter
  const filteredQ = useReadinessProducts(isFiltered ? channelId : null, isFiltered ? {
    page, page_size: MERGED_PAGE_SIZE,
    issue_type: issueFilter?.issue_type, issue_field: issueFilter?.field,
    issue_message: issueFilter?.message, missing_packshot: issueFilter?.missing_packshot,
    image_status: imageStatusFilter[0],
  } : undefined)

  const productIds = useMemo(() =>
    isFiltered
      ? (filteredQ.data?.scores.map(s => s.product_id) ?? [])
      : (productsQ.data?.products.map(p => p.id) ?? []),
  [isFiltered, filteredQ.data, productsQ.data])

  const packshotQ = usePackshotStatuses(productIds, channelId ?? undefined)
  const aiRefQ = useAIReferenceSummary(productIds)

  const products = useMemo((): ProductWithReadiness[] => {
    const pm = packshotQ.data
    const am = aiRefQ.data
    if (isFiltered) return (filteredQ.data?.scores ?? []).map(s => mergeFromScore(s, pm, am))
    const rm = buildReadinessMap(readinessQ.data?.scores ?? [])
    return (productsQ.data?.products ?? []).map(p => mergeProduct(p, rm, pm, am))
  }, [isFiltered, filteredQ.data, readinessQ.data, productsQ.data, packshotQ.data, aiRefQ.data])

  const total = isFiltered ? (filteredQ.data?.total ?? 0) : (productsQ.data?.total ?? 0)
  const totalPages = isFiltered
    ? Math.ceil(total / MERGED_PAGE_SIZE)
    : (productsQ.data?.total_pages ?? 0)

  return {
    products, total, totalPages,
    isLoading: isFiltered ? filteredQ.isLoading : productsQ.isLoading,
    isFetching: isFiltered ? filteredQ.isFetching : productsQ.isFetching,
  }
}
